import { type ComponentChildren, createContext } from 'preact';
import { useContext, useEffect, useId, useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { ArrowLeftIcon, CloseIcon } from '@xantory/icons';
import { Scrim } from '../Scrim/scrim';
import { Button } from '../Button/button';
import { useConfirm } from '../Confirm/confirm';
import { useFocusTrap } from '../_hooks/use-focus-trap';
import { useMountTransition } from '../_hooks/use-mount-transition';
import { styles } from './modal.styles';
import { useT } from '@xantory/i18n';

/** The modal's GUARDED close — the same path as Esc / outside-click / ✕, so an
 *  edited form asks before it is thrown away. */
const ModalCloseContext = createContext<() => void>(() => undefined);

/** A Cancel that closes through the discard guard — in the footer or the body. A plain
 *  `onClick={() => setOpen(false)}` is a programmatic close and skips it — which
 *  is how "Cancel" silently dropped typed input while ✕ and Esc asked
 *  (Approval Rules, 2026-09-29). */
export function ModalCancel({
  children
}: {
  children: ComponentChildren;
}) {
  const close = useContext(ModalCloseContext);
  return <Button variant="ghost" onClick={close}>
      {children}
    </Button>;
}
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** `lg` is for modals whose CONTENT is a wide table — a 10-column band grid
   *  cannot be read at `md`. Use it only when the width is needed. */
  size?: 'sm' | 'md' | 'detail' | 'wide' | 'lg';
  /** When false, Esc / outside-click won't close (the prototype `isEditing()` guard). */
  allowDismiss?: boolean;
  /** Renders a ← back arrow before the title (multi-step modals: detail → edit → pick). */
  onBack?: (() => void) | undefined;
  footer?: ComponentChildren;
  children: ComponentChildren;
}
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  allowDismiss = true,
  onBack,
  footer,
  children
}: ModalProps) {
  const t = useT('ui');
  const confirm = useConfirm();
  const {
    mounted,
    show
  } = useMountTransition(open);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // aria-modal promises contained focus — actually trap it (+ restore on close).
  useFocusTrap(dialogRef, show);

  // Dirty guard: the first edit inside the body arms a discard-confirm on every
  // UI-initiated close (Esc / outside-click / ✕). Programmatic closes — a form
  // calling onClose() after a successful save — never pass through here.
  const dirty = useRef(false);
  const asking = useRef(false);
  useEffect(() => {
    if (open) dirty.current = false;
  }, [open]);
  const markDirty = (e: Event) => {
    if (!(e.target instanceof Element) || e.target.closest('[data-dirty-ignore]')) return;
    dirty.current = true;
  };
  const requestClose = async () => {
    if (asking.current) return;
    if (dirty.current) {
      asking.current = true;
      const ok = await confirm({
        title: t('discardTitle'),
        message: t('discardMessage'),
        confirmLabel: t('discard'),
        danger: true
      });
      asking.current = false;
      if (!ok) return;
    }
    onClose();
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => {
    if (!open || !allowDismiss) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void requestCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, allowDismiss]);
  if (!mounted) return null;
  const bodyProps = stylex.props(styles.body);
  return <>
      <Scrim show={show} onClick={allowDismiss ? () => void requestClose() : undefined} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={title != null ? titleId : undefined} tabIndex={-1} {...stylex.props(styles.modal, size === 'sm' && styles.sm, size === 'detail' && styles.detail, size === 'wide' && styles.wide, size === 'lg' && styles.lg, show && styles.show)}>
        {(title != null || onBack != null) && <div {...stylex.props(styles.head)}>
            <div {...stylex.props(styles.headLeft)}>
              {onBack != null && <button type="button" aria-label={t('back')} onClick={onBack} {...stylex.props(styles.iconBtn)}>
                  <ArrowLeftIcon size={18} />
                </button>}
              {title != null && <h3 id={titleId} {...stylex.props(styles.title)}>
                  {title}
                </h3>}
            </div>
            <button type="button" aria-label={t('close')} onClick={() => void requestClose()} {...stylex.props(styles.iconBtn)}>
              <CloseIcon size={18} />
            </button>
          </div>}
        {/* Scrollbar hidden like Drawer's body (`.scroll-hidden`, reset.css) — the
            body still scrolls by wheel/touch/keyboard down to its actions. */}
        <div onInput={markDirty} onChange={markDirty} {...bodyProps} className={[bodyProps.className, 'scroll-hidden'].filter(Boolean).join(' ')}>
          {/* The body gets the guarded close too, not just the footer: a `ModalCancel`
              placed in a body action row used to read the context's no-op default, so
              the button rendered and did NOTHING (five dialogs, 2026-10-05). */}
          <ModalCloseContext.Provider value={() => void requestClose()}>
            {children}
          </ModalCloseContext.Provider>
        </div>
        {footer != null && <ModalCloseContext.Provider value={() => void requestClose()}>
            <div {...stylex.props(styles.foot)}>{footer}</div>
          </ModalCloseContext.Provider>}
      </div>
    </>;
}